import { Component, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { switchMap } from 'rxjs';
import { NotificationService } from '../../../core/services/notification.service';
import {
  DEFAULT_FEATURE,
  DEFAULT_FEEDBACK_TYPE,
  FEEDBACK_FEATURES,
  FEEDBACK_TYPES,
  FeedbackSubmit,
} from '../../../models/feedback.model';
import { FeedbackService } from '../feedback.service';

@Component({
  selector: 'app-feedback-form-dialog',
  standalone: true,
  imports: [
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
  ],
  templateUrl: './feedback-form-dialog.component.html',
  styleUrl: './feedback-form-dialog.component.scss',
})
export class FeedbackFormDialogComponent implements OnInit {
  private readonly feedbackSvc = inject(FeedbackService);
  private readonly notify = inject(NotificationService);
  private readonly ref = inject(MatDialogRef<FeedbackFormDialogComponent>);

  readonly features = FEEDBACK_FEATURES;
  readonly feedbackTypes = FEEDBACK_TYPES;

  feedbackType = DEFAULT_FEEDBACK_TYPE;
  feature = DEFAULT_FEATURE;
  rating = 0;
  comment = '';
  isAnonymous = false;
  selectedFile: File | null = null;
  previewUrl: string | null = null;
  submitting = signal(false);

  ngOnInit(): void {
    this.feedbackSvc.trackOpen().subscribe();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      this.notify.error('Only image files are allowed for screenshots');
      return;
    }
    this.selectedFile = file;
    const reader = new FileReader();
    reader.onload = (e) => {
      this.previewUrl = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  }

  removeFile(): void {
    this.selectedFile = null;
    this.previewUrl = null;
  }

  setRating(value: number): void {
    this.rating = this.rating === value ? 0 : value;
  }

  get isValid(): boolean {
    return !!this.feature && this.comment.trim().length > 0;
  }

  submit(): void {
    if (!this.isValid || this.submitting()) return;
    this.submitting.set(true);

    const doSubmit = (screenshotUrl?: string) => {
      const payload: FeedbackSubmit = {
        feedback_type: this.feedbackType,
        feature: this.feature,
        comment: this.comment.trim(),
        is_anonymous: this.isAnonymous,
        rating: this.rating || undefined,
        screenshot_url: screenshotUrl,
        page_url: window.location.href,
        browser_info: navigator.userAgent,
      };

      this.feedbackSvc.submitFeedback(payload).subscribe({
        next: () => {
          this.notify.success('Thank you for your feedback!');
          this.ref.close(true);
        },
        error: (err) => {
          this.notify.error(err?.error?.error ?? 'Failed to submit feedback');
          this.submitting.set(false);
        },
      });
    };

    if (this.selectedFile) {
      this.feedbackSvc
        .getScreenshotUploadUrl(this.selectedFile.name, this.selectedFile.type)
        .pipe(
          switchMap((res) =>
            this.feedbackSvc.uploadScreenshotToGcs(res.upload_url, this.selectedFile!).pipe(
              switchMap(() => {
                doSubmit(`https://storage.googleapis.com/${res.object_name}`);
                return [];
              }),
            ),
          ),
        )
        .subscribe({
          error: () => {
            this.notify.error('Screenshot upload failed, submitting without it');
            doSubmit();
          },
        });
    } else {
      doSubmit();
    }
  }
}
